import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  Trophy, Users, ChevronRight, ExternalLink, 
  Brain, Link2, Award, Star, Sparkles, Building2, Mail, Phone,
  User, Briefcase, MessageSquare, CheckCircle, ArrowRight,
  Leaf, Lightbulb, ChevronDown, Play, GraduationCap, MapPin
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Institution-Focused Winner Card Component
const WinnerCard = ({ rank, team, college, project, techStack, projectLink, videoLink, strengths, members, delay = 0 }) => {
  const [expanded, setExpanded] = useState(false);
  
  const getRankBadge = (rank) => {
    if (rank === 1) return { bg: 'from-yellow-400 via-yellow-500 to-amber-600', border: 'border-yellow-400/50', icon: '🥇', label: '1st Place', glow: 'shadow-[0_0_20px_rgba(250,204,21,0.3)]' };
    if (rank === 2) return { bg: 'from-slate-300 via-slate-400 to-slate-500', border: 'border-slate-400/50', icon: '🥈', label: '2nd Place', glow: 'shadow-[0_0_15px_rgba(148,163,184,0.3)]' };
    return { bg: 'from-amber-500 via-amber-600 to-amber-700', border: 'border-amber-500/50', icon: '🥉', label: '3rd Place', glow: 'shadow-[0_0_15px_rgba(245,158,11,0.3)]' };
  };
  
  const badge = getRankBadge(rank);
  
  return (
    <div 
      className={`group relative bg-gradient-to-br from-white/[0.08] to-white/[0.02] backdrop-blur-xl border ${badge.border} rounded-2xl overflow-hidden hover:border-purple-500/50 transition-all duration-500 hover:shadow-[0_0_60px_rgba(168,85,247,0.2)] hover:-translate-y-1`}
      style={{ animationDelay: `${delay}ms` }}
      data-testid={`winner-card-${team.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-purple-500/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
      
      {/* Content */}
      <div className="relative p-6">
        {/* Institution Name - PRIMARY FOCUS */}
        <div className="mb-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500/30 to-pink-500/30 rounded-xl flex items-center justify-center border border-purple-400/30 group-hover:scale-110 transition-transform duration-300">
              <GraduationCap className="text-purple-300" size={24} />
            </div>
            <div className="flex-1">
              <h3 className="text-xl md:text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-300 via-pink-300 to-purple-300 leading-tight">
                {college}
              </h3>
            </div>
          </div>
        </div>

        {/* Team Name */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Users size={16} className="text-gray-400" />
            <span className="text-lg font-semibold text-white">{team}</span>
          </div>
          
          {/* Rank Badge */}
          <div className={`bg-gradient-to-r ${badge.bg} px-3 py-1.5 rounded-full flex items-center gap-1.5 ${badge.glow}`}>
            <span className="text-base">{badge.icon}</span>
            <span className="font-bold text-xs text-white">{badge.label}</span>
          </div>
        </div>

        {/* Team Members - Now more prominent */}
        <div className="mb-4 p-3 bg-white/5 rounded-lg border border-white/5">
          <div className="flex items-center gap-2 mb-2">
            <Users size={14} className="text-purple-400" />
            <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">Team Members</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {members.slice(0, 4).map((member, idx) => (
              <span key={idx} className="px-2 py-1 bg-purple-500/10 rounded-md text-xs text-purple-200 font-medium">
                {member}
              </span>
            ))}
            {members.length > 4 && (
              <span className="px-2 py-1 bg-white/5 rounded-md text-xs text-gray-400">
                +{members.length - 4} more
              </span>
            )}
          </div>
        </div>

        {/* Project Title */}
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-1">
            <Lightbulb size={14} className="text-amber-400" />
            <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">Project</span>
          </div>
          <h4 className="text-lg font-bold text-white">{project}</h4>
        </div>

        {/* Tech Stack */}
        <div className="mb-4">
          <div className="flex flex-wrap gap-1.5">
            {techStack.slice(0, 5).map((tech, idx) => (
              <span key={idx} className="px-2 py-1 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-md text-xs text-purple-300 font-medium">
                {tech}
              </span>
            ))}
            {techStack.length > 5 && (
              <span className="px-2 py-1 bg-white/5 rounded-md text-xs text-gray-400">
                +{techStack.length - 5}
              </span>
            )}
          </div>
        </div>

        {/* View Project Button */}
        {projectLink && (
          <a 
            href={projectLink} 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-purple-500/20 to-pink-500/20 hover:from-purple-500/30 hover:to-pink-500/30 border border-purple-500/30 rounded-xl text-sm text-purple-200 font-medium transition-all group-hover:border-purple-400/50"
          >
            <ExternalLink size={16} />
            View Project
            {videoLink && (
              <span className="ml-2 px-2 py-0.5 bg-pink-500/30 rounded text-xs text-pink-200">+ Demo</span>
            )}
          </a>
        )}

        {/* Expandable Strengths */}
        {strengths && strengths.length > 0 && (
          <div className="mt-4 pt-4 border-t border-white/5">
            <button
              onClick={() => setExpanded(!expanded)}
              className="w-full flex items-center justify-between py-1 text-gray-400 hover:text-white transition-colors"
            >
              <span className="text-xs font-medium uppercase tracking-wide flex items-center gap-2">
                <Star size={12} className="text-yellow-400" />
                Key Strengths
              </span>
              <ChevronDown size={16} className={`transition-transform duration-300 ${expanded ? 'rotate-180' : ''}`} />
            </button>
            
            {expanded && (
              <div className="pt-3 animate-fadeIn">
                <ul className="space-y-1.5">
                  {strengths.map((strength, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-400">
                      <CheckCircle size={14} className="text-green-400 mt-0.5 flex-shrink-0" />
                      {strength}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Domain Section Component
const DomainSection = ({ icon: Icon, title, color, gradient, winners }) => {
  return (
    <section className="relative py-12" data-testid={`domain-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      {/* Domain Header */}
      <div className="flex items-center gap-4 mb-10">
        <div className={`w-14 h-14 bg-gradient-to-br ${gradient} rounded-2xl flex items-center justify-center shadow-lg`}>
          <Icon className="text-white" size={26} />
        </div>
        <div>
          <h2 className="text-2xl md:text-3xl font-black text-white">{title}</h2>
          <p className="text-gray-400 mt-0.5 text-sm">{winners.length} winning {winners.length === 1 ? 'team' : 'teams'}</p>
        </div>
      </div>
      
      {/* Winners Grid */}
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
        {winners.map((winner, idx) => (
          <WinnerCard key={idx} {...winner} delay={idx * 100} />
        ))}
      </div>
    </section>
  );
};

// Hero Images from EvalentX 1st Edition
const HERO_IMAGES = {
  main: [
    'https://customer-assets.emergentagent.com/job_1e895467-3e1d-455f-8ad1-2ca44657e699/artifacts/b7dmb8fh__PAC0245.JPG',
    'https://customer-assets.emergentagent.com/job_1e895467-3e1d-455f-8ad1-2ca44657e699/artifacts/n2b0t8l3__PAC0239.JPG',
    'https://customer-assets.emergentagent.com/job_1e895467-3e1d-455f-8ad1-2ca44657e699/artifacts/qlznzqh4__PAC0200.jpg'
  ],
  strip: [
    'https://customer-assets.emergentagent.com/job_1e895467-3e1d-455f-8ad1-2ca44657e699/artifacts/b7dmb8fh__PAC0245.JPG',
    'https://customer-assets.emergentagent.com/job_1e895467-3e1d-455f-8ad1-2ca44657e699/artifacts/n2b0t8l3__PAC0239.JPG',
    'https://customer-assets.emergentagent.com/job_1e895467-3e1d-455f-8ad1-2ca44657e699/artifacts/4fm7rull__PAC0016.jpg',
    'https://customer-assets.emergentagent.com/job_1e895467-3e1d-455f-8ad1-2ca44657e699/artifacts/epyaxsza__PAC0173.jpg'
  ]
};

const WinnersPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    organization: '',
    email: '',
    phone: '',
    team: '',
    institution: '',
    purpose: '',
    proposal: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const formRef = useRef(null);

  // All teams for dropdown with institution
  const allTeamsWithInstitution = [
    { team: 'Team Simple - SkillScreen AI', institution: 'Dharmsinh Desai University' },
    { team: 'Team Neural Coders - SignBridge', institution: 'L.D. College of Engineering' },
    { team: 'Team Syntex Error - ParkSense', institution: 'Gati Shakti Vishwavidyalaya' },
    { team: 'Team Neural Forge - AI Assessment Platform', institution: 'Charusat University' },
    { team: 'Team Apex - HireMinds AI', institution: 'Silver Oak University' },
    { team: 'Team Penguins Impossible - IdentiX', institution: 'Nirma University' },
    { team: 'Team Blockchain_Disruptors - BhoomiSetu', institution: 'Ahmedabad University' },
    { team: 'Team Synergy 067 - Dhruva', institution: 'Nirma University' },
    { team: 'Team FarmXpert - FarmXpert', institution: 'Vishwakarma Government Engineering College' },
    { team: 'Team FarmerEngage - FarmerEngage', institution: 'Ahmedabad Institute of Technology' },
    { team: 'Team Circuit 25 - Smart Onion Storage', institution: 'Dwarkadas J Sanghvi College of Engineering' },
    { team: 'Team Heisenberg - Nebula Cloud', institution: 'Charusat University' },
    { team: 'Team Quint Logic - JANTRAC InsightX', institution: 'Darshan University, Rajkot' }
  ];

  const handleTeamSelect = (teamValue) => {
    const selected = allTeamsWithInstitution.find(t => t.team === teamValue);
    setFormData(prev => ({
      ...prev,
      team: teamValue,
      institution: selected?.institution || ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/api/investor-connect`, formData);
      toast.success('Your inquiry has been submitted successfully! We will connect you with the team soon.');
      setFormData({
        name: '',
        organization: '',
        email: '',
        phone: '',
        team: '',
        institution: '',
        purpose: '',
        proposal: ''
      });
    } catch (err) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Winners Data
  const aiWinners = [
    {
      rank: 1,
      team: 'Team Simple',
      college: 'Dharmsinh Desai University',
      project: 'SkillScreen AI',
      techStack: ['Next.js', 'Node.js', 'Express.js', 'Gemini API', 'Socket.io', 'OpenCV'],
      projectLink: 'https://drive.google.com/drive/u/0/folders/1gLtnKBxvyNlBbC5wHNaSwkAoZYioY-jN',
      strengths: [
        'End-to-end workflow: JD → Skill Extraction → Assessment → Evaluation → Ranking',
        'Fraud detection (resume mismatch, bot detection, plagiarism)',
        'Modern full-stack architecture',
        'Recruiter analytics dashboard'
      ],
      members: ['Pruthviraj Parmar', 'Maharshi Jani', 'Harsh Manek', 'Riddhi Ladva']
    },
    {
      rank: 2,
      team: 'Team Neural Coders',
      college: 'L.D. College of Engineering',
      project: 'SignBridge',
      techStack: ['MediaPipe', 'Flask', 'Python', 'NumPy', 'Pandas', 'Scikit-learn', 'Google Translate API', 'OpenCV'],
      projectLink: 'https://signbridge-frontend.onrender.com/',
      videoLink: 'https://drive.google.com/drive/folders/1VBT2X8oE2oEuQQVKCFVDwk3CCYOvS6lH',
      strengths: [
        'Bidirectional ISL communication',
        'Multi-language support',
        'Real-time landmark detection',
        'Practical fallback keyboard mode'
      ],
      members: ['Laksh Tank', 'Nishtha Shah', 'Kunj Vaghani', 'Megh Patel', 'Dhyeya Patel', 'Aryan Khokhar']
    },
    {
      rank: 2,
      team: 'Team Syntex Error',
      college: 'Gati Shakti Vishwavidyalaya',
      project: 'ParkSense',
      techStack: ['Python', 'HTML', 'Shell', 'FastAPI'],
      projectLink: 'https://anmol-2-4.github.io/ParkSense_/',
      strengths: [
        'Real-time inference system',
        'Smart-city integration',
        'Confidence-based predictions'
      ],
      members: ['Hari Om Mishra', 'Anmol Gupta', 'Sagar Yadav', 'Prem Prabhakar', 'Pawan Kaduba Sapkal']
    },
    {
      rank: 3,
      team: 'Team Neural Forge',
      college: 'Charusat University',
      project: 'AI-Enabled Intelligent Assessment Platform',
      techStack: ['Next.js', 'Tailwind CSS', 'Supabase', 'OpenAI API', 'OpenRouter API'],
      projectLink: 'https://assessai-neural-forge.vercel.app/',
      strengths: [
        'JD parsing → Evaluation → Leaderboards',
        'Anti-cheat detection',
        'Explainable AI scoring'
      ],
      members: ['Tirth Ganatra', 'Anshul Jangid', 'Evan Gregor', 'Ved Patel']
    },
    {
      rank: 3,
      team: 'Team Apex',
      college: 'Silver Oak University',
      project: 'HireMinds AI',
      techStack: ['React 18', 'TypeScript', 'Vite', 'Supabase', 'PostgreSQL', 'Monaco Editor', 'WebRTC', 'ElevenLabs'],
      projectLink: 'https://hireminds.univoid.tech/',
      strengths: [
        'Six-agent autonomous hiring pipeline',
        'AI voice/video interviews',
        'Enterprise SaaS model',
        'Explainable AI reports'
      ],
      members: ['Abhishek Agarwal', 'Heer Patel', 'Valani Aastha', 'Vishva Patel']
    }
  ];

  const blockchainWinners = [
    {
      rank: 1,
      team: 'Team Penguins Impossible',
      college: 'Nirma University',
      project: 'IdentiX',
      techStack: ['Node.js', 'Express.js', 'Veramo', 'MongoDB', 'Solidity', 'Ethereum Testnet', 'React', 'TailwindCSS'],
      projectLink: 'https://identix-frontend.onrender.com/',
      strengths: [
        'Complete DID/VC lifecycle',
        'On-chain anchoring',
        'QR verification'
      ],
      members: ['Vedant Patel', 'Vihaan Patel', 'Anurag Pillai', 'Hiya Vashi', 'Manav Vala']
    },
    {
      rank: 3,
      team: 'Team Blockchain_Disruptors',
      college: 'Ahmedabad University',
      project: 'BhoomiSetu',
      techStack: ['React', 'Node.js', 'Hyperledger Fabric', 'Solidity', 'Web3.js', 'MetaMask', 'QGIS'],
      projectLink: 'https://drive.google.com/drive/folders/1NB0nilYRR_0Up9eSIkb--_tJgMFObBkx',
      strengths: [
        'Land registry on blockchain',
        'Multi-layer verification',
        'GIS integration'
      ],
      members: ['Saumya Shah', 'Suryaraj Jadeja', 'Om Patel']
    },
    {
      rank: 3,
      team: 'Team Synergy 067',
      college: 'Nirma University',
      project: 'Dhruva',
      techStack: ['React', 'Blockchain', 'Smart Contracts'],
      projectLink: 'https://dhruva-8.netlify.app/',
      strengths: [
        'Decentralized solution',
        'Smart contract automation',
        'User-friendly interface'
      ],
      members: ['Tirth Patel', 'Dhruv Matariya', 'Divy Mevada', 'Smit Faldu', 'Jenin Patel']
    }
  ];

  const agritechWinners = [
    {
      rank: 1,
      team: 'Team FarmXpert',
      college: 'Vishwakarma Government Engineering College',
      project: 'FarmXpert',
      techStack: ['Python', 'React', 'Machine Learning', 'IoT'],
      projectLink: 'https://drive.google.com/drive/folders/1l9rYkhTbDXIcCcjpFohdn071HTUxiKHE',
      strengths: [
        'Comprehensive farm management',
        'AI-powered crop recommendations',
        'Weather integration'
      ],
      members: ['Neel Sutariya', 'Dhrumil J. Kharadi', 'Patel Nirja Dipakkumar', 'Patel Nishva Kalpeshkumar']
    },
    {
      rank: 3,
      team: 'Team FarmerEngage',
      college: 'Ahmedabad Institute of Technology',
      project: 'FarmerEngage',
      techStack: ['React', 'Node.js', 'Express'],
      projectLink: 'https://drive.google.com/file/d/1hPKNhe8u2YNvlZSPLsYDgGc7TLc1xW0A/view',
      strengths: [
        'Farmer community platform',
        'Knowledge sharing',
        'Market linkage'
      ],
      members: ['Archi Rathod', 'Harsh Rasiklal Boghani', 'Akshata Kapre']
    }
  ];

  const openInnovationWinners = [
    {
      rank: 1,
      team: 'Team Circuit 25',
      college: 'Dwarkadas J Sanghvi College of Engineering',
      project: 'Smart Onion Storage System',
      techStack: ['React Native', 'Arduino IDE', 'Python', 'TypeScript', 'JavaScript'],
      projectLink: 'https://drive.google.com/drive/folders/10A3fie2q5QRYXR4EK1WrZ4W2jG78p8eM',
      strengths: [
        'IoT-based monitoring',
        'Automated climate control',
        'Mobile app integration',
        'Reduces post-harvest losses'
      ],
      members: ['Kalash Paras Bheda', 'Arnav Bhandari', 'Heet Bharadwa', 'Param Shah', 'Monil Jain']
    },
    {
      rank: 2,
      team: 'Team Heisenberg',
      college: 'Charusat University',
      project: 'Nebula Cloud',
      techStack: ['Next.js', 'MongoDB', 'React Flow', 'TypeScript', 'TailwindCSS'],
      projectLink: 'https://nebula-cloud-seven.vercel.app/',
      strengths: [
        'Visual workflow builder',
        'Cloud infrastructure',
        'Modern tech stack'
      ],
      members: ['Manav Merja', 'Dev Jivani']
    },
    {
      rank: 3,
      team: 'Team Quint Logic',
      college: 'Darshan University, Rajkot',
      project: 'JANTRAC InsightX',
      techStack: ['React', 'Node.js', 'TypeScript', 'MongoDB', 'Socket.IO'],
      projectLink: null,
      strengths: [
        'Real-time tracking',
        'Data analytics',
        'Scalable architecture'
      ],
      members: ['Nikhil Kashmeeraben Rupala', 'Darshil Dobariya', 'Niraj Thummar', 'Dhairy Mehta', 'Manav Delvadiya', 'Prince Karoliya', 'Harshraj Sinh', 'Jenil Vegad']
    }
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-white overflow-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(168,85,247,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(168,85,247,0.015)_1px,transparent_1px)] bg-[size:100px_100px]"></div>
        <div className="absolute top-0 left-1/4 w-[800px] h-[800px] bg-purple-500/8 rounded-full blur-[200px] animate-pulse"></div>
        <div className="absolute bottom-0 right-1/4 w-[800px] h-[800px] bg-pink-500/8 rounded-full blur-[200px] animate-pulse" style={{ animationDelay: '1.5s' }}></div>
        <div className="absolute top-1/3 right-0 w-[500px] h-[500px] bg-blue-500/5 rounded-full blur-[150px]"></div>
      </div>

      {/* Hero Section - Boxy Two-Part Layout */}
      <section className="relative px-6 pt-24 pb-8" data-testid="winners-hero">
        <div className="relative z-10 max-w-7xl mx-auto">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm mb-8" aria-label="Breadcrumb">
            <Link to="/" className="text-gray-400 hover:text-white transition-colors">Home</Link>
            <ChevronRight size={14} className="text-gray-600" />
            <span className="text-purple-400 font-medium">Winners – 1st Edition</span>
          </nav>

          {/* Two-Column Boxy Layout */}
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            
            {/* Left Box - Content Section */}
            <div className="bg-gradient-to-br from-white/[0.06] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-3xl p-8 md:p-10 shadow-[0_8px_32px_rgba(0,0,0,0.3)] hover:border-purple-500/30 transition-all duration-500">
              {/* Badge */}
              <div className="inline-flex items-center gap-3 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-full px-5 py-2.5 mb-6 backdrop-blur-sm">
                <GraduationCap className="text-purple-400" size={18} />
                <span className="text-purple-300 font-bold tracking-wide text-xs uppercase">Institutional Excellence</span>
                <Sparkles className="text-purple-400" size={14} />
              </div>
              
              {/* Main Heading */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-4">
                <span className="block bg-clip-text text-transparent bg-gradient-to-r from-white via-purple-100 to-white">
                  Winners
                </span>
                <span className="block text-2xl sm:text-3xl mt-2 bg-clip-text text-transparent bg-gradient-to-r from-purple-400 via-pink-400 to-purple-400">
                  1st Edition
                </span>
              </h1>
              
              {/* Subheading */}
              <p className="text-lg md:text-xl text-gray-300 font-light mb-4">
                Recognizing outstanding innovation from leading institutions.
              </p>
              
              {/* Description */}
              <p className="text-base text-gray-400 leading-relaxed mb-8">
                Our first edition brought together exceptional talent from India's premier institutions. 
                These winning teams demonstrated <span className="text-purple-300 font-medium">collaborative excellence</span> and 
                showcased <span className="text-pink-300 font-medium">global-standard innovation</span> across AI, Blockchain, Agritech, and beyond.
              </p>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/register"
                  className="group relative px-6 py-3.5 bg-gradient-to-r from-purple-500 to-pink-600 rounded-xl font-bold text-base hover:shadow-[0_0_40px_rgba(168,85,247,0.5)] transition-all duration-300 inline-flex items-center justify-center gap-2 overflow-hidden"
                  data-testid="cta-register-hackathon"
                >
                  <span className="absolute inset-0 bg-gradient-to-r from-pink-500 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                  <span className="relative flex items-center gap-2">
                    <Trophy size={18} />
                    Register for 2nd Edition
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
                
                <Link
                  to="/ai-league"
                  className="group px-6 py-3.5 bg-gradient-to-r from-white/5 to-white/10 border border-cyan-400/30 rounded-xl font-bold text-base hover:border-cyan-400/60 hover:bg-white/10 transition-all duration-300 inline-flex items-center justify-center gap-2"
                  data-testid="cta-register-ai-league"
                >
                  <Brain size={18} className="text-cyan-400" />
                  <span>Register for AI League</span>
                  <ArrowRight size={16} className="text-cyan-400 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
            
            {/* Right Box - Visual Section */}
            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                {/* Main Large Image */}
                <div className="col-span-2 relative rounded-2xl overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.4)] border border-white/10 group">
                  <img 
                    src={HERO_IMAGES.main[0]}
                    alt="EvalentX Winners - Team celebrating with trophy"
                    className="w-full h-56 md:h-64 object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="eager"
                    onLoad={() => setImagesLoaded(true)}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                  <div className="absolute bottom-3 left-3 flex items-center gap-2">
                    <div className="bg-gradient-to-r from-purple-500 to-pink-600 rounded-full px-3 py-1 text-xs font-bold flex items-center gap-1">
                      <Trophy size={12} />
                      1st Place Winners
                    </div>
                  </div>
                </div>
                
                {/* Secondary Images */}
                <div className="relative rounded-xl overflow-hidden shadow-lg border border-white/10 group">
                  <img 
                    src={HERO_IMAGES.main[1]}
                    alt="EvalentX - Award ceremony"
                    className="w-full h-36 md:h-44 object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="eager"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                </div>
                
                <div className="relative rounded-xl overflow-hidden shadow-lg border border-white/10 group">
                  <img 
                    src={HERO_IMAGES.main[2]}
                    alt="EvalentX - Team collaboration"
                    className="w-full h-36 md:h-44 object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="eager"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                </div>
              </div>
              
              {/* Decorative Elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-full blur-2xl"></div>
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-gradient-to-br from-cyan-500/15 to-blue-500/15 rounded-full blur-2xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Image Strip Section - Showcasing Winning Moments */}
      <section className="relative px-6 py-12" data-testid="winners-image-strip">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-8">
            <h2 className="text-xl md:text-2xl font-bold text-white mb-2">Moments of Innovation & Excellence</h2>
            <p className="text-gray-400 text-sm">Winning teams, collaboration, and breakthrough innovations</p>
          </div>
          
          {/* Image Strip Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {HERO_IMAGES.strip.map((img, idx) => (
              <div 
                key={idx}
                className="relative rounded-xl overflow-hidden shadow-lg border border-white/10 group cursor-pointer"
                style={{ 
                  animationDelay: `${idx * 150}ms`,
                  animation: 'fadeSlideUp 0.6s ease-out forwards',
                  opacity: 0
                }}
              >
                <img 
                  src={img}
                  alt={`EvalentX moment ${idx + 1}`}
                  className="w-full h-40 md:h-48 object-cover group-hover:scale-110 transition-transform duration-700"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute inset-0 border-2 border-purple-500/0 group-hover:border-purple-500/50 rounded-xl transition-all duration-300"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Scroll to Winners Indicator */}
      <div className="text-center pb-8">
        <div className="animate-bounce inline-flex flex-col items-center gap-2 text-gray-500">
          <span className="text-xs uppercase tracking-widest">Explore Winners</span>
          <ChevronDown size={24} />
        </div>
      </div>

      {/* Domain Sections */}
      <main className="relative z-10 max-w-7xl mx-auto px-6 pb-24">
        <DomainSection 
          icon={Brain}
          title="Artificial Intelligence"
          color="purple"
          gradient="from-purple-500 to-pink-600"
          winners={aiWinners}
        />
        
        <DomainSection 
          icon={Link2}
          title="Blockchain"
          color="blue"
          gradient="from-blue-500 to-cyan-600"
          winners={blockchainWinners}
        />
        
        <DomainSection 
          icon={Leaf}
          title="Agritech"
          color="green"
          gradient="from-green-500 to-emerald-600"
          winners={agritechWinners}
        />
        
        <DomainSection 
          icon={Lightbulb}
          title="Open Innovation"
          color="amber"
          gradient="from-amber-500 to-orange-600"
          winners={openInnovationWinners}
        />

        {/* Investor Connect Section */}
        <section className="relative mt-20 pt-16" id="investor-connect" ref={formRef}>
          {/* Background Glow */}
          <div className="absolute inset-0 bg-gradient-to-b from-emerald-500/5 via-transparent to-transparent rounded-3xl"></div>
          
          <div className="relative">
            {/* Section Header */}
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-3 bg-gradient-to-r from-emerald-500/10 to-green-500/10 border border-emerald-500/20 rounded-full px-6 py-3 mb-6 backdrop-blur-sm">
                <Briefcase className="text-emerald-400" size={18} />
                <span className="text-emerald-400 font-bold tracking-wide text-sm">INVESTOR & RECRUITER CONNECT</span>
              </div>
              
              <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
                Interested in These Teams?
              </h2>
              
              <p className="text-base text-gray-400 max-w-xl mx-auto">
                Connect with our winning teams for hiring, investment, mentorship, or partnership opportunities.
              </p>
            </div>
            
            {/* Form Card */}
            <div className="max-w-2xl mx-auto">
              <div className="bg-gradient-to-br from-white/[0.06] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-6 md:p-8">
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1.5">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all text-sm"
                        placeholder="John Doe"
                        data-testid="investor-name-input"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1.5">
                        Organization Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.organization}
                        onChange={(e) => setFormData(prev => ({ ...prev, organization: e.target.value }))}
                        className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all text-sm"
                        placeholder="Acme Ventures"
                        data-testid="investor-org-input"
                      />
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1.5">
                        Email *
                      </label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                        className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all text-sm"
                        placeholder="john@acme.com"
                        data-testid="investor-email-input"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1.5">
                        Phone *
                      </label>
                      <input
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                        className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all text-sm"
                        placeholder="+91 98765 43210"
                        data-testid="investor-phone-input"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1.5">
                      Select Team *
                    </label>
                    <select
                      required
                      value={formData.team}
                      onChange={(e) => handleTeamSelect(e.target.value)}
                      className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all appearance-none cursor-pointer text-sm"
                      data-testid="investor-team-select"
                    >
                      <option value="" className="bg-gray-900">Select a team...</option>
                      {allTeamsWithInstitution.map(item => (
                        <option key={item.team} value={item.team} className="bg-gray-900">
                          {item.institution} - {item.team.split(' - ')[0]}
                        </option>
                      ))}
                    </select>
                    {formData.institution && (
                      <p className="mt-1.5 text-xs text-purple-400 flex items-center gap-1">
                        <GraduationCap size={12} />
                        {formData.institution}
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1.5">
                      Purpose *
                    </label>
                    <select
                      required
                      value={formData.purpose}
                      onChange={(e) => setFormData(prev => ({ ...prev, purpose: e.target.value }))}
                      className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all appearance-none cursor-pointer text-sm"
                      data-testid="investor-purpose-select"
                    >
                      <option value="" className="bg-gray-900">Select purpose...</option>
                      <option value="hire" className="bg-gray-900">Hire Team Members</option>
                      <option value="invest" className="bg-gray-900">Invest in Project</option>
                      <option value="mentor" className="bg-gray-900">Offer Mentorship</option>
                      <option value="partnership" className="bg-gray-900">Partnership / Collaboration</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1.5">
                      Expectations / Proposal Details *
                    </label>
                    <textarea
                      required
                      rows={3}
                      value={formData.proposal}
                      onChange={(e) => setFormData(prev => ({ ...prev, proposal: e.target.value }))}
                      className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all resize-none text-sm"
                      placeholder="Tell us about your interest in this team..."
                      data-testid="investor-proposal-input"
                    />
                  </div>
                  
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-3 bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg font-bold text-base hover:shadow-[0_0_30px_rgba(168,85,247,0.4)] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="investor-submit-btn"
                  >
                    {submitting ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        Submit Inquiry
                        <ArrowRight size={18} />
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative mt-20 text-center">
          <div className="bg-gradient-to-r from-purple-500/5 via-pink-500/5 to-purple-500/5 border border-purple-500/10 rounded-2xl p-10">
            <h3 className="text-2xl md:text-3xl font-black text-white mb-3">
              Ready to Be the Next Winner?
            </h3>
            <p className="text-gray-400 text-base max-w-xl mx-auto mb-6">
              Join EvalentX 2nd Edition and showcase your innovation at IIT Gandhinagar.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/hackathon"
                className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-600 rounded-full font-bold text-sm hover:shadow-[0_0_30px_rgba(168,85,247,0.4)] transition-all inline-flex items-center justify-center gap-2"
              >
                Explore 2nd Edition
                <ArrowRight size={16} />
              </Link>
              <Link
                to="/register"
                className="px-6 py-3 bg-white/5 border border-white/10 rounded-full font-bold text-sm hover:bg-white/10 transition-all"
              >
                Register Now
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* CSS for animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        @keyframes fadeSlideUp {
          from { 
            opacity: 0; 
            transform: translateY(20px); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }
      `}</style>
    </div>
  );
};

export default WinnersPage;
